'use client';

import { Button } from '@/components/ui/button';
import { areaAPI } from '@/lib/api';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

interface PropertyCategory {
    id: string;
    title: string;
    image: string;
    description: string;
    link: string;
}



// Function to get relevant image for each category
function getImageForCategory(index: number): string {
    switch (index) {
        case 0:
            return "/images/palmjebelalidubai-547033-170507.jpg"; // Luxury villa with city view - Downtown Dubai
        case 1:
            return "/images/businessbaydubai-402729-130928.jpg"; // Beachfront villa - Palm Jumeirah
        case 2:
            return "/images/downtown-dubai.jpg"; // Modern waterfront villa - Marina
        case 3:
            return "/images/dubai-marina.jpg"; // Beach villa - JBR
        case 4:
            return "/images/downtown-dubai.jpg"; // Luxury city villa - Mall area
        case 5:
            return "/images/palm-jumeirah.jpg"; // Traditional villa - Historic area
        default:
            return "/images/palmjebelalidubai-547033-170507.jpg"; // Default villa image
    }
}

export default function PropertiesNearby() {
    const router = useRouter();
    const [hoveredCard, setHoveredCard] = useState<number | null>(null);
    const [areas, setAreas] = useState<any[]>([]);

    const fetchAreas = async () => {
        try {
            const resp = await areaAPI.getFrontendList();
            const raw = (resp as any)?.data;
            const list = (raw?.data ?? raw?.items ?? raw?.results ?? raw) as any[];
            setAreas(list);
        } catch (error) {
            console.error('Error fetching areas:', error);
        }
    }

    useEffect(() => {
        fetchAreas();
    }, []);

    return (
        <section className="py-20 bg-white">
            <div className="container mx-auto px-4">
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-6">Properties Nearby Famous <span className="text-primary">Tourist Places</span></h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Stay close to Dubai&apos;s most iconic attractions and experience luxury accommodations near world-famous landmarks
                    </p>
                </motion.div>

                <div className="flex flex-col lg:flex-row h-[500px] gap-2 overflow-hidden rounded">
                    {areas?.slice(0, 6)?.map((area, index) => (
                        <motion.div
                            key={index}
                            className="relative overflow-hidden cursor-pointer group"
                            initial={{ flex: "1 1 0%" }}
                            animate={{
                                flex: hoveredCard === index ? "3 1 0%" : hoveredCard !== null ? "0.5 1 0%" : "1 1 0%",
                            }}
                            transition={{ duration: 0.6, ease: "easeInOut" }}
                            onMouseEnter={() => setHoveredCard(index)}
                            onMouseLeave={() => setHoveredCard(null)}
                        >
                            <div
                                className="absolute inset-0 bg-cover bg-center"
                                style={{
                                    // backgroundImage: `url(${area.image})`,
                                    backgroundImage: `url(${getImageForCategory(index)})`,
                                }}
                            >
                                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300"></div>
                            </div>

                            <div className="relative h-full flex flex-col justify-end p-8 text-white">
                                <motion.div
                                    initial={{ opacity: 1 }}
                                    animate={{
                                        opacity: hoveredCard === null || hoveredCard === index ? 1 : 0.7,
                                    }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <h3 className="text-2xl lg:text-3xl font-medium mb-2 leading-tight">{area.name}</h3>

                                    <motion.p
                                        className="text-white/90 text-sm lg:text-base"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{
                                            opacity: hoveredCard === index ? 1 : 0,
                                            height: hoveredCard === index ? "auto" : 0,
                                        }}
                                        transition={{ duration: 0.4, delay: hoveredCard === index ? 0.2 : 0 }}
                                    >
                                        {area.description}
                                    </motion.p>

                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{
                                            opacity: hoveredCard === index ? 1 : 0,
                                            y: hoveredCard === index ? 0 : 20,
                                        }}
                                        transition={{ duration: 0.4, delay: hoveredCard === index ? 0.3 : 0 }}
                                        className="mt-4"
                                    >
                                        <Button
                                            onClick={() => router.push(`/listings?area=${area.externalId}`)}
                                        // variant="outline"
                                        // className="bg-white/20 border-white/30 text-white hover:bg-white/30 backdrop-blur-sm"
                                        >
                                            View Properties
                                        </Button>
                                    </motion.div>
                                </motion.div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}